import { Add, Edit } from "@mui/icons-material";
import {
    Button,
    IconButton,
    Switch,
    Tooltip
} from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { API_URLS } from "../../config/APIUrls";
import axiosInstance from "../../config/axios";
import CustomDialog from "../../Shared/CustomDialogBox";
import CustomFilter from "../../Shared/CustomForFiler";
import CustomTable from "../../Shared/CustomTable";
import CustomToPagination from "../../Shared/CustomToPagination";

const ZoneMaster = () => {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);

    const filterFormik = useFormik({
        initialValues: {
            search: '',
            start_date: '',
            end_date: ''
        },
        enableReinitialize: true
    });

    const { data: zoneListData, isLoading: loading } = useQuery(
        ["get_zone_master", page, filterFormik.values.search, filterFormik.values.start_date, filterFormik.values.end_date],
        () => axiosInstance.get(API_URLS.get_zone_master, {
            params: {
                search: filterFormik.values.search,
                start_date: filterFormik.values.start_date,
                end_date: filterFormik.values.end_date,
                page: page,
                count: 10,
            },
        }),
        {
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false
        }
    );

    const zoneList = zoneListData?.data?.response || [];

    const { data: managerData } = useQuery(
        "get_zone_manager",
        () => axiosInstance.get(API_URLS.get_zone_manager,
             { params:
                 { count: 1000000 ,
                    status: "1"
                 } }),
        { refetchOnMount: true, refetchOnReconnect: true, refetchOnWindowFocus: true }
    );

    const managerOptions = managerData?.data?.response?.data || [];

    // Fetch Districts
    const { data: districtData } = useQuery(
        "get_district_master",
        () => axiosInstance.get(API_URLS.get_district_master, { params: { count: 1000000 ,status: "1" } }),
         { refetchOnMount: true, refetchOnReconnect: true, refetchOnWindowFocus: true }
    );

    const districtOptions = districtData?.data?.response?.data || [];

    // Formik for create/edit Zone
    const fk = useFormik({
        initialValues: {
            name: "",
            address: "N/A",
            district: "",
            manager: "",
            status: "1"
        },
        onSubmit: async (values) => {
            if (editData) return await updateZone(values);
            else return await createZone(values);
        },
    });

    // Create Zone
    const createZone = async (values) => {
        try {
            const reqBody = {
                m05_zn_name: values.name,
                m05_zn_address: values.address,
                m05_zn_district: values.district,
                m05_zn_manger: values.manager,
                m05_zn_status:"1"
            };
            const res = await axiosInstance.post(API_URLS.create_zone_master, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_zone_master");
                handleClose();
            }
        } catch (e) {
            toast.error("Failed to create zone");
        }
    };

    // Update Zone
    const updateZone = async (values) => {
        try {
            const reqBody = {
                m05_zn_id: editData.m05_zn_id,
                m05_zn_name: values.name,
                m05_zn_address: values.address,
                m05_zn_district: values.district,
                m05_zn_manger: values.manager,
                m05_zn_status: editData.m05_zn_status
            };
            const res = await axiosInstance.post(API_URLS.update_zone_master, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_zone_master");
                handleClose();
            }
        } catch (e) {
            toast.error("Failed to update zone");
        }
    };

    // Toggle Zone Status
    const toggleStatus = async (row, checked) => {
        try {
            const reqBody = {
                m05_zn_id: row.m05_zn_id,
                // m05_zn_status: checked ? "1" : "0"
            };
            const res = await axiosInstance.post(API_URLS.update_zone_status, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_zone_master");
            }
        } catch (e) {
            toast.error("Failed to update status");
        }
    };

   const handleExport = async () => {
        try {
            const res = await axiosInstance.get(API_URLS.get_zone_excel, {
                  search: filterFormik.values.search || "",
                start_date: filterFormik.values.start_date || "",
                end_date: filterFormik.values.end_date || "",
            })
            const fileUrl = res?.data?.response?.url;
            window.open(fileUrl, "_blank")
        }
        catch (e) {
            console.log("Something went wrong")
        }
    }

    // Dialog handlers
    const handleOpen = () => {
        fk.resetForm();
        setEditData(null);
        setOpen(true);
    };

    const handleEdit = (row) => {
        setEditData(row);
        fk.setValues({
            name: row.m05_zn_name,
            address: row.m05_zn_address,
            district: String(row.m05_zn_district),
            manager: String(row.m05_zn_manger),
            status: String(row.m05_zn_status)
        });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditData(null);
    };

    const tableHead = ["S.No", "Block Name", "Address", "District", "Manager", "Status", "Actions", "Date"];
    const tableRows = zoneList?.data?.map((item, index) => [
        <span>{index + 1}</span>,
        <span>{item.m05_zn_name || "--"}</span>,
        <span>{item.m05_zn_address || "--"}</span>,
        <span>{item.m04_dist_name || "--"}</span>,
        <span>{item.m01_full_name || "--"}</span>,
        <Switch
            checked={item.m05_zn_status === 1}
            onChange={(e) => toggleStatus(item, e.target.checked)}
            color="success"
        />,
        <div className="flex justify-center gap-3">
            <Tooltip title="Edit">
                <IconButton onClick={() => handleEdit(item)}>
                    <Edit className="text-blue-600" />
                </IconButton>
            </Tooltip>
        </div>,
        <span>{moment(item?.m05_zn_created_at).format("DD-MM-YYYY")}</span>
    ]);

    return (
        <div className="mx-5">
            <div className="flex justify-between items-center p-4 ">
                <p className="font-bold text-xl">Block Master</p>
                 <Button variant="contained" className="!bg-[#00008b]" 
                    startIcon={<Add />}
                    onClick={handleOpen}
                >
                    Add 
                </Button>
            </div>

            <CustomFilter
                formik={filterFormik}
                onFilter={() => { setPage(1); queryClient.invalidateQueries("get_zone_master"); }}
                onClear={() => { filterFormik.resetForm(); setPage(1); queryClient.invalidateQueries("get_zone_master"); }}
                onExport={handleExport}
            />

            <CustomTable tablehead={tableHead} tablerow={tableRows} isLoading={loading} />

            <CustomToPagination setPage={setPage} page={page} data={zoneList} />

            <CustomDialog
                open={open}
                onClose={handleClose}
                onSubmit={fk.handleSubmit}
                title={editData ? "Edit Block" : "Add Block"}
                formik={fk}
                fields={[
                    { name: "name", label: "Block Name" },
                    { name: "address", label: "Address" },
                    {
                        name: "district",
                        label: "District",
                        type: "select",
                        options: districtOptions.map(d => ({ label: d.m04_dist_name, value: String(d.m04_dist_id) }))
                    },
                    {
                        name: "manager",
                        label: "Select Block Manager",
                        type: "select",
                        options: managerOptions.map(m => ({ label: m.m01_full_name, value: String(m.m01_reg_id) }))
                    },
                   
                    
                ]}
            />
        </div>
    );
};

export default ZoneMaster;
